import { X, Minus, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { cldImage } from "@/lib/cldImage";

const UPGRADE = {
  1: { size: 2, prompt: "One more and it's for two." },
  2: { size: 4, prompt: "Two more makes it a Sharing Box." },
  4: { size: 6, prompt: "Two more — For the Occasion." },
};

function BoxBreakdown({ selections }) {
  if (!selections?.length) return null;
  const grouped = selections.reduce((acc, s) => ({ ...acc, [s]: (acc[s] || 0) + 1 }), {});
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-espresso-light">
      {Object.entries(grouped).map(([f, n]) => (
        <li key={f}><span className="text-gold mr-1">·</span>{f} × {n}</li>
      ))}
    </ul>
  );
}

function Upsell({ item, onNavigate }) {
  const size = item.selections?.length;
  const up = UPGRADE[size];
  if (!up || item.category !== "boxes") return null;
  return (
    <Link to={`/box/${up.size}`} onClick={onNavigate}
      data-testid={`drawer-upsell-${size}`}
      className="mt-3 block border border-gold/40 bg-gold/5 px-3 py-2 hover:bg-gold/10 transition-colors">
      <p className="font-serif italic text-xs text-espresso">{up.prompt}</p>
      <p className="label-xs link-underline text-espresso mt-1">Upgrade →</p>
    </Link>
  );
}

export default function CartDrawer() {
  const { items, subtotal, updateQty, removeItem, drawerOpen, setDrawerOpen } = useCart();
  if (!drawerOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[55] bg-espresso/40 transition-opacity duration-200"
        onClick={() => setDrawerOpen(false)}
        data-testid="cart-drawer-overlay"
      />
      <aside
        data-testid="cart-drawer"
        className="fixed right-0 top-0 bottom-0 z-[60] flex w-full flex-col bg-ivory animate-fade-up sm:w-[440px]"
      >
        <div className="flex items-center justify-between p-6 border-b border-line">
          <div className="label-xs">Your basket</div>
          <button onClick={() => setDrawerOpen(false)} data-testid="cart-drawer-close">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 && (
            <p className="text-espresso-light font-serif italic text-xl mt-16 text-center">
              Your basket is quietly waiting.
            </p>
          )}
          {items.map((it) => {
            const rawSrc = it.image_url?.startsWith("/api/")
              ? `${process.env.REACT_APP_BACKEND_URL}${it.image_url}`
              : it.image_url;
            const src = cldImage(rawSrc, { w: 200 });
            return (
              <div key={it._key} className="flex gap-4" data-testid={`cart-item-${it.slug}`}>
                <div className="w-20 h-24 bg-ivory-100 overflow-hidden flex-shrink-0">
                  {src && <img src={src} alt={it.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="font-serif text-lg text-espresso leading-tight">{it.name}</h4>
                      {it.flavor && <div className="text-xs text-espresso-light mt-1">{it.flavor}</div>}
                      {it.category === "boxes" ? (
                        <BoxBreakdown selections={it.selections} />
                      ) : (
                        it.selections?.length > 0 && (
                          <div className="text-xs text-espresso-light mt-1">{it.selections.join(", ")}</div>
                        )
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(it._key)}
                      className="text-espresso-light hover:text-espresso text-xs flex-shrink-0"
                      data-testid={`cart-remove-${it.slug}`}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-1 border border-line rounded-full">
                      <button onClick={() => updateQty(it._key, it.quantity - 1)} data-testid={`cart-dec-${it.slug}`} aria-label="Decrease quantity" className="tap-target text-espresso">
                        <Minus size={16} strokeWidth={1.5} />
                      </button>
                      <span className="w-6 text-center text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it._key, it.quantity + 1)} data-testid={`cart-inc-${it.slug}`} aria-label="Increase quantity" className="tap-target text-espresso">
                        <Plus size={16} strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="font-serif text-lg text-espresso">₹{it.price * it.quantity}</div>
                  </div>
                  {it.savings > 0 && (
                    <div className="mt-1 text-xs text-gold-hover text-right" data-testid={`drawer-savings-${it.slug}`}>
                      Bundle savings −₹{it.savings * it.quantity}
                    </div>
                  )}
                  <Upsell item={it} onNavigate={() => setDrawerOpen(false)} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-4 border-t border-line p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-6">
          {items.some((x) => x.savings > 0) && (
            <div className="flex justify-between items-baseline text-gold-hover" data-testid="drawer-bundle-total">
              <span className="label-xs">Bundle savings</span>
              <span className="font-serif text-sm">
                −₹{items.reduce((s, x) => s + (x.savings || 0) * x.quantity, 0)}
              </span>
            </div>
          )}
          <div className="flex justify-between items-baseline">
            <span className="label-xs">Subtotal</span>
            <span className="font-serif text-2xl text-espresso" data-testid="cart-subtotal">₹{subtotal}</span>
          </div>
          <p className="text-xs text-espresso-light">Delivery, taxes and promotions applied at checkout.</p>
          <Link
            to="/checkout"
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-checkout-btn"
            className={`btn-espresso w-full block text-center ${items.length === 0 ? "opacity-40 pointer-events-none" : ""}`}
          >
            Proceed to checkout
          </Link>
          <button
            onClick={() => setDrawerOpen(false)}
            className="w-full text-xs label-xs text-espresso-light hover:text-espresso"
          >
            Continue shopping
          </button>
        </div>
      </aside>
    </>
  );
}
